export const MAX_DIMENSION = 1600;

// Interface for serialized file data that can be stored in IndexedDB
export interface SerializedFile {
  name: string;
  type: string;
  lastModified: number;
  data: string; // base64 data URL
}

/**
 * Deserialize a SerializedFile back to a File object
 */
export const deserializeFile = async (
  serialized: SerializedFile,
): Promise<File> => {
  // Convert base64 data URL to blob
  const response = await fetch(serialized.data);
  const blob = await response.blob();

  return new File([blob], serialized.name, {
    type: serialized.type,
    lastModified: serialized.lastModified,
  });
};

export const deserializeFiles = async (
  serialized: SerializedFile[],
): Promise<File[]> => {
  return Promise.all(serialized.map(deserializeFile));
};

export const refreshFile = async (file: File): Promise<File> => {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified || Date.now(),
  });
};

const THUMBNAIL_MAX_SIZE = 200;
const THUMBNAIL_QUALITY = 0.6;

export const generateThumbnail = async (file: File): Promise<string> => {
  const fresh = await refreshFile(file);
  const bitmap = await createImageBitmap(fresh);
  try {
    let { width, height } = bitmap;
    const max = THUMBNAIL_MAX_SIZE;
    if (width > height) {
      height = Math.round((height * max) / width);
      width = max;
    } else {
      width = Math.round((width * max) / height);
      height = max;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No 2d context");
    ctx.drawImage(bitmap, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/jpeg", THUMBNAIL_QUALITY);
    canvas.width = 0;
    canvas.height = 0;
    return dataUrl;
  } finally {
    bitmap.close();
  }
};

export const downscaleImage = async (file: File): Promise<File> => {
  const buffer = await file.arrayBuffer();
  const freshBlob = new Blob([buffer], { type: file.type });
  const bitmap = await createImageBitmap(freshBlob);

  let width = bitmap.width;
  let height = bitmap.height;

  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    if (width > height) {
      height = Math.round((height * MAX_DIMENSION) / width);
      width = MAX_DIMENSION;
    } else {
      width = Math.round((width * MAX_DIMENSION) / height);
      height = MAX_DIMENSION;
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx?.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return new Promise((resolve, reject) => {
    const outputType = "image/jpeg";
    const outputName = file.name.replace(/\.[^/.]+$/, ".jpg");
    canvas.toBlob(
      (blob) => {
        // Release canvas memory
        canvas.width = 0;
        canvas.height = 0;

        if (blob) {
          resolve(
            new File([blob], outputName, {
              type: outputType,
              lastModified: file.lastModified,
            }),
          );
        } else {
          reject(new Error("Failed to create blob from canvas"));
        }
      },
      outputType,
      0.7,
    );
  });
};
